import { Bar, Line } from "@rio-cloud/rio-uikit";
import ComposedChart from "@rio-cloud/rio-uikit/ComposedChart";
import ChartTooltip from "@rio-cloud/rio-uikit/ChartTooltip";
import Legend from "@rio-cloud/rio-uikit/Legend";
import ReferenceLine from "@rio-cloud/rio-uikit/ReferenceLine";
import Card from "@rio-cloud/rio-uikit/Card";
import { formatValue } from "../../../utils/format";

interface FinancialEvolutionItem {
  month: string;
  expenses: number;
  revenue: number;
  profit: number;
}

interface FinancialEvolutionChartProps {
  data: FinancialEvolutionItem[];
}

export default function FinancialEvolutionChart({ data }: FinancialEvolutionChartProps) {
  const maxRevenue = Math.max(...data.map((item) => item.revenue));
  const minExpenses = Math.min(...data.map((item) => item.expenses));

  const step = Math.ceil(maxRevenue / 300000) * 100000;

  const yAxisMax = Math.ceil(maxRevenue / step) * step;
  const yAxisMin = Math.floor(minExpenses / step) * step;

  const positiveCount = yAxisMax / step;
  const negativeCount = Math.abs(yAxisMin / step);

  const ticks = [];
  for (let i = positiveCount; i >= -negativeCount; i--) {
    ticks.push(i * step);
  }

  return (
    <Card className="padding-y-25 padding-x-50 margin-bottom-20">
      <div className="display-flex align-items-center justify-content-between gap-10 padding-bottom-20">
        <span className="text-size-16 text-bold">Evolução</span>
      </div>

      <div className="display-flex justify-content-center align-items-center">
        <div className="width-100pct">
          <ComposedChart
            width={1200}
            height={400}
            data={data}
            barGap={-28}
            bars={[
              <Bar
                key="revenue"
                name="Receitas"
                dataKey="revenue"
                barSize={28}
                fill="#CCECCE"
                radius={[4, 4, 0, 0]}
                style={{ fill: "#CCECCE" }}
              />,
              <Bar
                key="expenses"
                name="Despesas"
                dataKey="expenses"
                barSize={28}
                fill="#F4C7C0"
                radius={[4, 4, 0, 0]}
                style={{ fill: "#F4C7C0" }}
              />,
            ]}
            lines={[
              <Line
                key="profit"
                name="Lucro"
                type="monotone"
                dataKey="profit"
                strokeWidth={3}
                stroke="#30B4C0"
                dot={{ fill: "#fff", r: 4 }}
                activeDot={{ fill: "#30B4C0", r: 6, stroke: "none" }}
              />,
            ]}
            legend={
              <Legend
                iconSize={12}
                align="right"
                layout="horizontal"
                verticalAlign="bottom"
                payload={[
                  { value: "Despesas", type: "rect", color: "#F4C7C0" },
                  { value: "Receitas", type: "rect", color: "#CCECCE" },
                  { value: "Lucro", type: "line", color: "#30B4C0" },
                ]}
                formatter={(value: string) => {
                  return <span className="text-color-dark margin-left-10 margin-right-10">{value}</span>;
                }}
              />
            }
            tooltip={
              <ChartTooltip
                content={(props: any) => {
                  if (!props.active || !props.payload || props.payload.length === 0) return null;

                  const data = props.payload[0].payload;
                  const profit = data.profit;
                  const revenue = data.revenue;
                  const expenses = data.expenses;
                  const marginValue = revenue > 0 ? (profit / revenue) * 100 : 0;
                  const margin = marginValue % 1 === 0 ? marginValue.toFixed(0) : marginValue.toFixed(2);

                  return (
                    <div className="display-flex flex-column gap-5 padding-y-10 padding-x-15 rounded border bg-white text-center">
                      <span>Receitas: {formatValue(revenue)}</span>
                      <span>Despesas: {formatValue(expenses)}</span>
                      <span className="text-bold">Lucro: {formatValue(profit)}</span>
                      <span>Margem de lucro: {margin}%</span>
                    </div>
                  );
                }}
              />
            }
            showXAxis
            showYAxis
            showGrid={false}
            yAxisOptions={{
              domain: [yAxisMin, yAxisMax],
              axisLine: { stroke: "#2A3740" },
              tickFormatter: (value: number) =>
                value === 0
                  ? "0"
                  : `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
              width: 100,
              ticks: ticks,
            }}
            xAxisOptions={{
              dataKey: "month",
              axisLine: false,
              tickLine: false,
            }}
            containerOptions={{ width: "100%", height: 400 }}
            referenceLines={[<ReferenceLine key="zero" y={0} stroke="#2A3740" strokeWidth={2} />]}
          />
        </div>
      </div>
    </Card>
  );
}
